<script lang="ts" context="module">
  export interface Content {
    json: any;
    text: string;
    title: string;
  }
</script>

<script lang="ts">
  import VersionHistoryModal from "$components/knowledge-base/VersionHistoryModal.svelte";
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import IconButton from "$components/+design-system/IconButton/IconButton.svelte";
  import MainPanelContainer from "app/components/+layout/MainPanelContainer/MainPanelContainer.svelte";
  import Reader from "../editor/Reader.svelte";
  import DocumentEditor from "./DocumentEditor.svelte";
  import PublishEditDocSection from "../knowledge-base/PublishEditDocSection.svelte";
  import { RightAside, RightAsideFilled, Lock } from "$components/+shared/OVIcon";
  import { createEventDispatcher } from "svelte";
  import { community, versionHistoryFlag } from "$stores/community";
  import { isCommunityMember } from "$stores/permissions";
  import { sidePanel } from "app/stores/sidePanel";
  import { getContent } from "app/components/editor/utils";
  import { writable, type Writable } from "svelte/store";
  import { createDocumentService, DocumentService } from "$service/documentService";
  import { alerts } from "$stores/alert";
  import { DocumentTypeEnum } from "../../models/documents";
  import { type UpdateContent, type YdocStore } from "$components/editor";
  import type { JSONContent } from "@tiptap/core";
  import type { UserStore } from "$stores/user";
  import type { WritableDocument } from "app/models/documents";

  export let title = "";
  export let canEdit: boolean;
  export let color = "";
  export let content: JSONContent;
  export let doc: WritableDocument | undefined = undefined;
  export let ydoc: YdocStore | null;
  export let user: UserStore;
  export let onSaveDoc: (
    json: any,
    text: string,
    ydoc: Uint8Array,
    title: string,
    isSavingDraft: boolean
  ) => Promise<{ json: any } | undefined>;
  export let isPublished = false;
  export let docType: DocumentTypeEnum = DocumentTypeEnum.Document;
  export let readOnly = true;
  export let versionHistoryModalOpen: Writable<boolean> = writable(false);
  export let reader: Reader | undefined = undefined;

  const dispatch = createEventDispatcher();

  let isSaving = false;
  let contentUpdate: UpdateContent = { ...getContent(content), text: "", markdown: "" };
  let documentService: DocumentService;

  $: if (ydoc) {
    documentService = createDocumentService({
      ydoc,
      contentUpdate,
      onSaveDoc,
      user: $user,
      docType,
      versionHistoryFlag: $versionHistoryFlag,
      versionHistoryModalOpen,
      alerts,
    });
  }

  // exit edit mode when viewing version history
  $: if ($versionHistoryModalOpen) {
    readOnly = true;
    documentService.saveSpace(true).catch(() => {});
  }
</script>

<MainPanelContainer hideLogin>
  <div slot="heading" class="flex items-center gap-2 overflow-hidden">
    <slot name="title">
      <span class="truncate">
        {title}
      </span>
    </slot>
    <slot name="dropdown" />
  </div>

  <svelte:fragment slot="action-buttons">
    <!-- Publish Edit section -->
    {#if canEdit}
      <PublishEditDocSection
        bind:readOnly
        {isSaving}
        {docType}
        {ydoc}
        {sidePanel}
        {documentService}
        bind:isPublished
        versionHistoryFlag={$versionHistoryFlag}
      />
    {/if}

    {#if docType !== DocumentTypeEnum.Document && $community.isPrivate && !$isCommunityMember}
      <div class="flex items-center text-sm text-gray-700 dark:text-gray-400">
        <OVIcon icon={Lock} />
        <span class="ml-1.5">Private community</span>
      </div>
    {/if}

    <!-- Aside Toggle -->
    {#if docType === DocumentTypeEnum.Document}
      <IconButton
        icon={$sidePanel.visible ? RightAsideFilled : RightAside}
        a11yLabel={`${$sidePanel.visible ? "Collapse" : "Expand"} doc details [⇧ P]`}
        variant="tertiary"
        on:click={sidePanel.toggleExpansion}
      />
    {/if}
  </svelte:fragment>

  <DocumentEditor
    bind:reader
    bind:readOnly
    {content}
    {ydoc}
    {user}
    {docType}
    {canEdit}
    {color}
    hasContentsBlock={contentUpdate.hasContentsBlock}
    onUpdate={(detail) => {
      contentUpdate = { ...contentUpdate, ...detail };
      content = detail.json;
      dispatch("update", detail);
    }}
    onSaveDraft={async () => {
      await documentService.saveSpace(true);
    }}
  >
    <slot name="footer" slot="footer" hasContentsBlock={contentUpdate.hasContentsBlock} />
  </DocumentEditor>

  <svelte:fragment slot="bottom-floating-element">
    <slot name="pill" />
  </svelte:fragment>
</MainPanelContainer>

{#if $user.isAuthed && ydoc && doc && $versionHistoryModalOpen}
  <VersionHistoryModal
    user={$user}
    bind:open={$versionHistoryModalOpen}
    on:create={() => ($versionHistoryModalOpen = false)}
    {doc}
    {ydoc}
  />
{/if}
